import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FEATURED_PROFILES } from "config/api-const";
import { PLACEHOLDER_IMAGE } from "constant";
import "./featured-bar.css";

const PLAN_BADGE = {
  Platinum: { label: "Platinum ✦", cls: "fb-badge-platinum" },
  Gold:     { label: "Gold ★",     cls: "fb-badge-gold" },
  Silver:   { label: "Silver ◈",   cls: "fb-badge-silver" },
};

const FeaturedBar = () => {
  const [profiles, setProfiles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [offset, setOffset]       = useState(0);
  const [isPaused, setIsPaused]   = useState(false);
  const navigate                  = useNavigate();

  // Fetch once on mount
  useEffect(() => {
    axios
      .get(FEATURED_PROFILES)
      .then((res) => {
        if (res.data.success) {
          const valid = (res.data.profiles || []).filter(
            (p) => p.userId
          );
          setProfiles(valid);
        }
      })
      .catch((err) => console.error("[FeaturedBar] fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  // Auto-scroll every 4 s
  const advance = useCallback(() => {
    if (!profiles.length) return;
    setOffset((prev) => (prev + 1) % profiles.length);
  }, [profiles.length]);

  useEffect(() => {
    if (isPaused || profiles.length <= 1) return;
    const id = setInterval(advance, 4000);
    return () => clearInterval(id);
  }, [isPaused, advance, profiles.length]);

  if (loading || profiles.length === 0) return null;

  const profile = profiles[offset];
  const badge   = PLAN_BADGE[profile.plan] || PLAN_BADGE.Silver;

  // Build info rows matching the reference image
  const horoscopeRow = [profile.gotra, profile.rasi, profile.nakshatra]
    .filter(Boolean)
    .join(" | ");

  const locationRow = [profile.city, profile.state]
    .filter(Boolean)
    .join(", ");

  return (
    <aside
      className="fb-root"
      aria-label="Featured profiles"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      <div className="fb-header">
        <span className="fb-header-icon">⭐</span>
        <span className="fb-header-title">FEATURED PROFILES</span>
        <span className="fb-header-icon">⭐</span>
        {/* <span className="fb-header-sub">Silver · Gold · Platinum members</span> */}
      </div>

      {/* Card row */}
      <div className="fb-card-row">
        {/* Prev button */}
        <button
          className="fb-nav fb-nav-prev"
          aria-label="Previous featured profile"
          onClick={() =>
            setOffset((p) => (p === 0 ? profiles.length - 1 : p - 1))
          }
        >
          &#8250;
        </button>

        {/* Horizontal card */}
        <div
          className={`fb-card ${badge.cls}`}
          onClick={() => navigate(`/members/${profile.userId}`)}
          onKeyDown={(e) =>
            e.key === "Enter" && navigate(`/members/${profile.userId}`)
          }
          tabIndex={0}
          role="button"
          aria-label={`View ${profile.name}'s profile`}
        >
          {/* Avatar */}
          <div className="fb-avatar-wrap">
            <img
              src={profile.profileImage || PLACEHOLDER_IMAGE[100]}
              alt={profile.name}
              className="fb-avatar"
              loading="lazy"
            />
          </div>

          {/* Details */}
          <div className="fb-details">
            {/* Name + ID + degree + age */}
            <p className="fb-name">
              {profile.name}
              {profile.userId && (
                <span className="fb-id"> (ID: {profile.userId})</span>
              )}
              {profile.degree && (
                <span className="fb-degree"> {profile.degree}</span>
              )}
              {profile.age && (
                <span className="fb-age"> | Age: {profile.age}</span>
              )}
            </p>

            {/* Gotra | Rasi | Nakshatra */}
            {horoscopeRow && (
              <p className="fb-meta">
                {profile.gotra && <><strong>Gotra:</strong> {profile.gotra} | </>}
                {profile.rasi && <><strong>Rasi:</strong> {profile.rasi} | </>}
                {profile.nakshatra && <><strong>Nakshatra:</strong> {profile.nakshatra}</>}
              </p>
            )}

            {/* Occupation */}
            {profile.occupation && (
              <p className="fb-meta">
                <strong>Occupation:</strong> {profile.occupation}
              </p>
            )}

            {/* HomeTown */}
            {locationRow && (
              <p className="fb-meta">
                <strong>HomeTown:</strong> {locationRow}
              </p>
            )}
          </div>

          {/* Plan badge */}
          <span className={`fb-badge ${badge.cls}`}>{badge.label}</span>
        </div>

        {/* Next button */}
        <button
          className="fb-nav fb-nav-next"
          aria-label="Next featured profile"
          onClick={() =>
            setOffset((p) => (p + 1) % profiles.length)
          }
        >
          &#8250;
        </button>
      </div>

      {/* Dots */}
      {profiles.length > 1 && (
        <div className="fb-dots" role="tablist" aria-label="Featured profile navigation">
          {profiles.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === offset}
              aria-label={`Go to profile ${i + 1}`}
              className={`fb-dot${i === offset ? " fb-dot-active" : ""}`}
              onClick={() => setOffset(i)}
            />
          ))}
        </div>
      )}
    </aside>
  );
};

export default FeaturedBar;
